import { useId } from "react";
import SitewrapLogo from "@/components/SitewrapLogo";
import "@/styles/dash-loader.css";

type DashLoaderSize = "page" | "compact" | "inline";

type DashLoaderProps = {
  size?: DashLoaderSize;
  label?: string;
  hint?: string;
  className?: string;
};

const MARK_SIZE: Record<DashLoaderSize, number> = {
  page: 22,
  compact: 16,
  inline: 12
};

export default function DashLoader({
  size = "page",
  label = "Loading this view",
  hint,
  className = ""
}: DashLoaderProps) {
  const gradId = `dashLoaderGrad-${useId().replace(/:/g, "")}`;
  const showMark = size !== "inline";
  const showCopy = size !== "inline" && Boolean(label);

  return (
    <div
      className={`dashLoader dashLoader--${size}${className ? ` ${className}` : ""}`}
      role="status"
      aria-live="polite"
      aria-label={label || "Loading"}
    >
      <div className="dashLoaderStage">
        {showMark ? <div className="dashLoaderGlow" aria-hidden="true" /> : null}
        <svg className="dashLoaderRing" viewBox="0 0 64 64" aria-hidden="true">
          <defs>
            <linearGradient id={gradId} x1="8" y1="4" x2="56" y2="60" gradientUnits="userSpaceOnUse">
              <stop offset="0" stopColor="#60a5fa" />
              <stop offset="0.5" stopColor="#818cf8" />
              <stop offset="1" stopColor="#34d399" stopOpacity="0.15" />
            </linearGradient>
          </defs>
          <circle cx="32" cy="32" r="27" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="2" />
          <circle
            className="dashLoaderArc"
            cx="32"
            cy="32"
            r="27"
            fill="none"
            stroke={`url(#${gradId})`}
            strokeWidth="2.75"
            strokeLinecap="round"
            strokeDasharray="42 128"
          />
        </svg>
        {showMark ? (
          <div className="dashLoaderMark">
            <SitewrapLogo size={MARK_SIZE[size]} showWordmark={false} />
          </div>
        ) : null}
      </div>
      {showCopy ? (
        <div className="dashLoaderCopy">
          <div className="dashLoaderLabel">
            {label}
            <span className="dashLoaderDots" aria-hidden="true">
              <span className="dashLoaderDot" />
              <span className="dashLoaderDot" />
              <span className="dashLoaderDot" />
            </span>
          </div>
          {hint ? <div className="dashLoaderHint">{hint}</div> : null}
        </div>
      ) : null}
    </div>
  );
}

export function DashUserSkeleton() {
  return (
    <>
      <span className="avatar dashUserSkelAvatar" aria-hidden="true" />
      <span className="userMeta" aria-label="Loading account">
        <span className="dashSkelLine dashSkelLine--lg" />
        <span className="dashSkelLine dashSkelLine--sm" />
      </span>
    </>
  );
}
